// eslint-disable-next-line no-restricted-imports -- this file defines the abstraction
import clsx, { type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge instance configured for the design-system's `@theme`
 * tokens (defined in `../system.css`).
 *
 * Tailwind v4 lets us register custom theme values — `--color-brand`,
 * `--spacing-default`, `--radius-tight`, etc. — that turn into utility
 * classes like `bg-brand`, `p-default`, `rounded-tight`. tw-merge
 * doesn't read system.css at runtime; it works from this hand-maintained
 * mirror to recognize which utility classes belong to the same conflict
 * group. Without an entry here, `cn('bg-brand', 'bg-danger')` would
 * emit BOTH classes instead of letting the later one win.
 *
 * ⚠️  When adding or removing a `@theme` token in system.css, mirror
 * the change in the matching theme group below. Keep the lists sorted
 * the same way as system.css so diffs are easy to spot.
 */
const merge = extendTailwindMerge({
    extend: {
        theme: {
            color: [
                'ai-1',
                'ai-2',
                'ai-alt-1',
                'ai-alt-2',
                'ai-contrast',
                'ai-weaker',
                'brand-contrast',
                'brand',
                'container-disabled',
                'container-hover',
                'container-inset',
                'container',
                'content-disabled',
                'content-mid',
                'content-weak',
                'content-weaker',
                'content-weakest',
                'content',
                'danger-contrast',
                'danger',
                'highlight',
                'info-contrast',
                'info',
                'layout-base',
                'line-strong',
                'line-weak',
                'line-weakest',
                'line',
                'scrim',
                'success-contrast',
                'success',
                'warning-contrast',
                'warning',
            ],
            spacing: [
                'container-0',
                'container-1',
                'container-2',
                'container-3',
                'cozy',
                'default',
                'field-max-w',
                'field',
                'interactive',
                'relaxed',
                'tight',
            ],
            radius: ['default', 'relaxed', 'tight'],
            ease: ['interactive', 'state'],
            text: [
                'ui-2xl',
                'ui-base',
                'ui-lg',
                'ui-sm',
                'ui-xl',
                // Typography roles (typography.css → Typography roles)
                'display',
                'heading-1',
                'heading-2',
                'heading-3',
                'card-title',
                'card-heading',
                'body',
                'body-emphasis',
                'caption',
                'subtle-heading',
                'table-group',
                'table-header-group',
            ],
        },
    },
});

export function cn(...inputs: ClassValue[]): string {
    return merge(clsx(...inputs));
}
